import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScenarioCodeLookup, CodeLookupResult } from './ScenarioCodeLookup';
import { Plus, Search } from 'lucide-react';
import { useBrand } from '@/config/brands';
import type { LocalScenarioProcedure } from '@/lib/scenarioStorage';

const PHASE_OPTIONS = ['Pre-Op', 'Surgery', 'Treatment', 'Recovery', 'Post-Op', 'Follow-Up', 'Emergency',
  'Prenatal Care', 'Labor & Delivery', 'Postpartum', 'Diagnostics', 'Rehabilitation', 'Physical Therapy'];

interface ProcedureFormProps {
  procedureCode: string;
  setProcedureCode: (v: string) => void;
  procedureCodeType: string;
  setProcedureCodeType: (v: string) => void;
  procedureDescription: string;
  setProcedureDescription: (v: string) => void;
  procedurePhase: string;
  setProcedurePhase: (v: string) => void;
  procedureQuantity: string;
  setProcedureQuantity: (v: string) => void;
  procedureCost: string;
  setProcedureCost: (v: string) => void;
  procedureSetting: 'inpatient' | 'outpatient' | 'unspecified';
  setProcedureSetting: (v: 'inpatient' | 'outpatient' | 'unspecified') => void;
  editingIndex: number | null;
  onAdd: () => void;
  onCancel: () => void;
  onCodeSelected: (result: CodeLookupResult) => void;
  showCollapse?: boolean;
  onCollapse?: () => void;
  procedures: LocalScenarioProcedure[];
}

export function ProcedureForm({
  procedureCode,
  setProcedureCode,
  procedureCodeType,
  setProcedureCodeType,
  procedureDescription,
  setProcedureDescription,
  procedurePhase,
  setProcedurePhase,
  procedureQuantity,
  setProcedureQuantity,
  procedureCost,
  setProcedureCost,
  procedureSetting,
  setProcedureSetting,
  editingIndex,
  onAdd,
  onCancel,
  onCodeSelected,
  showCollapse,
  onCollapse,
  procedures,
}: ProcedureFormProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  return (
    <div className={`border rounded-lg p-4 ${isAhp ? 'border-gray-200 bg-gray-50' : 'border-matrix-green/20 bg-black/10'}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className={`font-semibold text-sm ${isAhp ? 'text-gray-900' : 'font-mono text-matrix-green'}`}>
          {editingIndex !== null ? 'Edit Procedure' : 'Search & Add Procedure'}
        </h3>
        {showCollapse && procedures.length > 0 && (
          <button
            onClick={onCollapse}
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            Collapse
          </button>
        )}
      </div>

      <div className="mb-3">
        <Label className="text-xs mb-1 block">Search by code or description</Label>
        <ScenarioCodeLookup onCodeSelected={onCodeSelected} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Code Type</Label>
          <Select value={procedureCodeType} onValueChange={setProcedureCodeType}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CPT">CPT</SelectItem>
              <SelectItem value="DRG">DRG</SelectItem>
              <SelectItem value="HCPCS">HCPCS</SelectItem>
              <SelectItem value="REV">Revenue</SelectItem>
              <SelectItem value="Other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Code *</Label>
          <Input
            placeholder="e.g., 99213"
            value={procedureCode}
            onChange={(e) => setProcedureCode(e.target.value)}
            className="h-9 text-xs font-mono"
          />
        </div>

        <div className="col-span-2 space-y-1.5">
          <Label className="text-xs">Description *</Label>
          <Input
            placeholder="e.g., Office visit, Hip replacement"
            value={procedureDescription}
            onChange={(e) => setProcedureDescription(e.target.value)}
            className="h-9 text-xs"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Phase</Label>
          <Select value={procedurePhase} onValueChange={setProcedurePhase}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PHASE_OPTIONS.map(p => (
                <SelectItem key={p} value={p}>{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Setting</Label>
          <Select value={procedureSetting} onValueChange={(v) => setProcedureSetting(v as any)}>
            <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="inpatient">Inpatient</SelectItem>
              <SelectItem value="outpatient">Outpatient</SelectItem>
              <SelectItem value="unspecified">Unspecified</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Quantity</Label>
          <Input
            type="number"
            min="1"
            placeholder="1"
            value={procedureQuantity}
            onChange={(e) => setProcedureQuantity(e.target.value)}
            className="h-9 text-xs font-mono"
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Est. Cost (per unit)</Label>
          <Input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={procedureCost}
            onChange={(e) => setProcedureCost(e.target.value)}
            className="h-9 text-xs font-mono"
          />
        </div>
      </div>

      <div className="flex gap-2 mt-4">
        <Button
          onClick={onAdd}
          className={`flex-1 ${isAhp ? 'bg-civic-money hover:bg-[#234d3b] text-white' : 'bg-matrix-green text-black hover:bg-matrix-green/90'}`}
          size="sm"
        >
          <Plus className="w-4 h-4 mr-2" />
          {editingIndex !== null ? 'Update Procedure' : 'Add Procedure'}
        </Button>
        {editingIndex !== null && (
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}

/** Compact button to show the procedure form when collapsed */
export function ProcedureFormToggle({ onClick }: { onClick: () => void }) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';

  return (
    <Button
      variant="outline"
      onClick={onClick}
      className={`w-full ${isAhp ? 'border-civic-money/30 text-civic-money' : 'border-matrix-green/30 text-matrix-green'}`}
    >
      <Search className="w-4 h-4 mr-2" /> Search & Add Procedure
    </Button>
  );
}
